import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, Check, Mail } from "lucide-react";

const encode = (data) =>
  Object.keys(data)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join("&");

const ContactForm = () => {
  const [mountNode, setMountNode] = useState(null);
  const [status, setStatus] = useState("idle");
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });

  useEffect(() => {
    const footer = document.querySelector('[data-testid="footer"]');
    if (!footer?.parentNode) return undefined;

    let node = document.getElementById("contact-section-mount");
    const created = !node;

    if (!node) {
      node = document.createElement("div");
      node.id = "contact-section-mount";
    }

    footer.parentNode.insertBefore(node, footer);
    setMountNode(node);

    return () => {
      if (created && node.parentNode) node.parentNode.removeChild(node);
    };
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("sending");

    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", ...form }),
      });
      setStatus("success");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      setStatus("error");
    }
  };

  const formSection = (
    <section id="contact" className="relative py-28 md:py-36 border-t border-white/10">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#76b900]">Contact</span>
            <h2 className="font-display font-bold tracking-tighter text-4xl md:text-5xl mt-4 max-w-xl">
              Parlons de votre prochain projet.
            </h2>
            <p className="mt-5 text-white/60 text-lg font-light max-w-xl">
              Décrivez-nous votre besoin. Notre équipe vous répondra rapidement avec une première orientation claire.
            </p>
            <a href="mailto:info@carthyai.com" className="mt-8 inline-flex items-center gap-3 text-white/80 hover:text-[#76b900] transition-colors">
              <Mail size={18} className="text-[#76b900]" /> info@carthyai.com
            </a>
          </div>

          <div className="liquid-glass rounded-2xl p-6 md:p-8">
            {status === "success" ? (
              <div className="min-h-[360px] flex flex-col items-center justify-center text-center">
                <div className="h-14 w-14 rounded-full bg-[#76b900]/15 border border-[#76b900]/40 flex items-center justify-center">
                  <Check size={26} className="text-[#76b900]" />
                </div>
                <h3 className="font-display text-2xl font-semibold mt-5">Message envoyé</h3>
                <p className="text-white/60 mt-3 max-w-sm">Merci. Nous reviendrons vers vous dans les meilleurs délais.</p>
                <button type="button" onClick={() => setStatus("idle")} className="mt-7 text-sm text-[#76b900] hover:text-[#8ce500]">
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form name="contact" method="POST" data-netlify="true" netlify-honeypot="bot-field" onSubmit={handleSubmit} className="space-y-5">
                <input type="hidden" name="form-name" value="contact" />
                <p className="hidden"><label>Ne pas remplir : <input name="bot-field" /></label></p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <label className="text-sm text-white/70">Nom
                    <input required name="name" value={form.name} onChange={handleChange} className="mt-2 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-white outline-none focus:border-[#76b900]" placeholder="Votre nom" />
                  </label>
                  <label className="text-sm text-white/70">E-mail
                    <input required type="email" name="email" value={form.email} onChange={handleChange} className="mt-2 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-white outline-none focus:border-[#76b900]" placeholder="vous@entreprise.com" />
                  </label>
                </div>

                <label className="block text-sm text-white/70">Entreprise
                  <input name="company" value={form.company} onChange={handleChange} className="mt-2 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-white outline-none focus:border-[#76b900]" placeholder="Nom de votre entreprise" />
                </label>

                <label className="block text-sm text-white/70">Message
                  <textarea required name="message" value={form.message} onChange={handleChange} rows="6" className="mt-2 w-full resize-none rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-white outline-none focus:border-[#76b900]" placeholder="Parlez-nous de votre projet..." />
                </label>

                {status === "error" && <p className="text-sm text-red-400">Une erreur est survenue. Réessayez ou écrivez-nous directement.</p>}

                <button disabled={status === "sending"} type="submit" className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#76b900] px-7 py-3.5 text-sm font-semibold text-black hover:bg-[#8ce500] disabled:opacity-60 transition-colors">
                  {status === "sending" ? "Envoi..." : "Envoyer le message"}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );

  return mountNode ? createPortal(formSection, mountNode) : null;
};

export default ContactForm;
